import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Minus } from "lucide-react";
import axios from "axios";

const API_URL = "https://accessoriesgirly.onrender.com/api/products"; // Your real API

const cardVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function ProductSection() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await axios.get(API_URL);
        setProducts(res.data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  const updateCart = (updatedCart) => {
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("storage"));
  };

  const addToCart = (product) => {
    const existing = cart.find((item) => item.id === product._id);
    if (existing) {
      updateCart(
        cart.map((item) =>
          item.id === product._id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      updateCart([...cart, { ...product, id: product._id, quantity: 1 }]);
    }
  };

  const decreaseQty = (product) => {
    const existing = cart.find((item) => item.id === product._id);
    if (!existing) return;
    if (existing.quantity === 1) {
      updateCart(cart.filter((item) => item.id !== product._id));
    } else {
      updateCart(
        cart.map((item) =>
          item.id === product._id ? { ...item, quantity: item.quantity - 1 } : item
        )
      );
    }
  };

  const getQuantity = (productId) =>
    cart.find((item) => item.id === productId)?.quantity || 0;

  const handleBuyNow = (product) => {
    const existing = cart.find((item) => item.id === product._id);
    let updatedCart;
    if (existing) {
      updatedCart = cart.map((item) =>
        item.id === product._id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      updatedCart = [...cart, { ...product, id: product._id, quantity: 1 }];
    }
    updateCart(updatedCart);
    navigate("/cart");
  };

  const calculateDiscount = (price, offerPrice) => {
    if (!offerPrice || offerPrice >= price) return 0;
    return Math.round(((price - offerPrice) / price) * 100);
  };

  if (loading) {
    return <div className="py-12 text-center text-gray-600">Loading products...</div>;
  }

  const displayProducts = products.slice(0, 25); // Show only 25 initially

  return (
    <section className="py-12 bg-pink-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-3xl font-bold text-center text-pink-600 mb-10"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Our Products
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {displayProducts.map((product) => {
            const qty = getQuantity(product._id);
            const discount = calculateDiscount(product.price, product.offerPrice);

            return (
              <motion.div
                key={product._id}
                className="group relative bg-white border border-pink-200 cursor-pointer shadow-md rounded-2xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:-rotate-1 hover:shadow-2xl hover:border-pink-400"
                variants={cardVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                onClick={() => navigate(`/productInfo/${product._id}`)}
              >
                {/* Discount badge */}
                {discount > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                    {discount}% OFF
                  </span>
                )}

                <div className="overflow-hidden">
                  <motion.img
                    src={product.images?.[0] || "https://via.placeholder.com/300x200"}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                <div className="p-4 text-center">
                  <h3 className="text-lg font-semibold text-gray-800 group-hover:text-pink-600 transition-colors">
                    {product.name}
                  </h3>

                  <p className="text-pink-600 font-bold mt-2">
                    {product.offerPrice && product.offerPrice < product.price ? (
                      <>
                        <span className="line-through text-gray-400 mr-2">₹{product.price.toFixed(2)}</span>
                        <span>₹{product.offerPrice.toFixed(2)}</span>
                      </>
                    ) : (
                      <span>₹{product.price.toFixed(2)}</span>
                    )}
                  </p>

                  <div className="mt-4 flex justify-center gap-2">
                    {qty === 0 ? (
                      <button
                        className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent parent click
                          addToCart(product);
                        }}
                      >
                        Add to Cart
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button
                          className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            decreaseQty(product);
                          }}
                        >
                          <Minus className="w-4 h-4 text-gray-700" />
                        </button>
                        <span className="px-4 py-1 bg-pink-100 text-pink-700 font-bold rounded-lg">
                          {qty}
                        </span>
                        <button
                          className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product);
                          }}
                        >
                          <Plus className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    )}

                    <button
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyNow(product);
                      }}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </motion.div>

            );
          })}
        </div>

        {/* Explore More button */}
        {products.length > 25 && (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate("/products")}
              className="px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
            >
              Explore More
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
